import { checkNextPage } from '@/shared/lib/utils/pagination'
import i18n from '@/app/i18n'

import { TOAST_DISPATCHERS } from '@/shared/constants/store-constants/toast'
import paymentLinkHistoryApiService from '@/shared/services/payment-link-history-api.service'
import { PAYMENT_LINK_HISTORY_DISPATCHERS } from '@/shared/constants/store-constants/paymentLinkHistory'
import {
  getQuery,
  setQuery,
  setRangeQuery,
} from '@/shared/lib/utils/queryParams'
import {
  toUTCStartOfDay,
  toUTCEndOfDay,
  toDateString,
} from '@/shared/lib/utils/date'
import { downloadFile } from '@/shared/lib/utils/downloadFile'

export default {
  resetStore({ commit }) {
    commit('RESET_STORE')
  },

  resetFiltersAndSorting({ commit, dispatch }) {
    commit('RESET_FILTERS_AND_SORTING')

    setQuery({})

    dispatch(
      PAYMENT_LINK_HISTORY_DISPATCHERS.getPaymentLinksHistory,
      {},
      { root: true }
    )
  },

  onSearch({ dispatch, commit, state }, value) {
    commit('SET_SEARCH', value)
    commit('RESET_PAGE')

    const allParams = {
      ...state.filters,
      ...(value && { search: value }),
      ...state.sort,
    }

    if (state.range.length) {
      allParams.created_at_from = state.range[0]
      allParams.created_at_to = state.range[1]
    }

    setQuery(allParams)

    dispatch(
      PAYMENT_LINK_HISTORY_DISPATCHERS.getPaymentLinksHistory,
      {},
      { root: true }
    )
  },

  setRange({ commit, dispatch }, payload) {
    commit('RESET_PAGE')
    commit('SET_RANGE', payload)

    setRangeQuery(['created_at_from', 'created_at_to'], payload)

    dispatch(
      PAYMENT_LINK_HISTORY_DISPATCHERS.getPaymentLinksHistory,
      {},
      { root: true }
    )
  },

  getQueryParams({ commit, dispatch }) {
    const {
      search,
      created_at_from,
      created_at_to,
      sort_by,
      sort_direction,
      invoice_form_statuses,
      statuses,
      ...otherFilters
    } = getQuery(['invoice_form_statuses', 'statuses'])

    const filters = {
      invoice_form_statuses: invoice_form_statuses || [],
      statuses: statuses || [],
      ...otherFilters,
    }

    commit('SET_ALL_FILTERS', filters)
    commit('SET_SEARCH', search)

    if (sort_by && sort_direction) {
      commit('SET_SORTING', { sort_by, sort_direction })
    }

    if (
      created_at_from &&
      created_at_to &&
      !isNaN(new Date(created_at_from).getTime()) &&
      !isNaN(new Date(created_at_to).getTime())
    ) {
      const startDate = new Date(created_at_from)
      startDate.setHours(0, 0, 0, 0)
      const endDate = new Date(created_at_to)
      endDate.setHours(0, 0, 0, 0)
      commit('SET_RANGE', [startDate, endDate])
    }

    dispatch(
      PAYMENT_LINK_HISTORY_DISPATCHERS.getPaymentLinksHistory,
      {},
      { root: true }
    )
  },

  changePage({ commit, dispatch, state }) {
    if (checkNextPage(state.page, state.total, state.limit)) {
      commit('SET_PAGE', state.page + 1)

      dispatch(
        PAYMENT_LINK_HISTORY_DISPATCHERS.getPaymentLinksHistory,
        {},
        { root: true }
      )
    }
  },

  async getPaymentLinksHistory({ commit, state, dispatch }) {
    try {
      commit('SET_LOADING', true)

      const params = {
        page: state.page,
        limit: state.limit,
        ...(state.search && { search: state.search }),
        ...state.sort,
      }

      if (state.filters.invoice_form_statuses?.length) {
        params.invoice_form_statuses = state.filters.invoice_form_statuses
      }

      if (state.filters.statuses?.length) {
        params.statuses = state.filters.statuses
      }

      const [dateFrom, dateTo] = state.range

      if (dateFrom) {
        params.created_at_from = toUTCStartOfDay(dateFrom)
      }

      if (dateTo) {
        params.created_at_to = toUTCEndOfDay(dateTo)
      }

      const { data } =
        await paymentLinkHistoryApiService.getPaginatePaymentLinkHistory(params)

      commit('SET_ITEMS', data)
    } catch (error) {
      if (!error.isHandled) {
        dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  onSelectSort({ commit, dispatch, state }, payload) {
    commit('RESET_PAGE')
    commit('SET_SORTING', payload)

    const allParams = {
      ...state.filters,
      ...(state.search && { search: state.search }),
      ...payload,
    }

    if (state.range.length) {
      allParams.created_at_from = state.range[0]
      allParams.created_at_to = state.range[1]
    }

    setQuery(allParams)

    dispatch(
      PAYMENT_LINK_HISTORY_DISPATCHERS.getPaymentLinksHistory,
      {},
      { root: true }
    )
  },

  onSelectFilter({ commit, dispatch, state }, { key, value }) {
    commit('RESET_PAGE')
    commit('SET_FILTER', { key, value })

    const allParams = {
      ...state.filters,
      ...(state.search && { search: state.search }),
      ...state.sort,
    }

    if (state.range.length) {
      allParams.created_at_from = state.range[0]
      allParams.created_at_to = state.range[1]
    }

    setQuery(allParams)

    dispatch(
      PAYMENT_LINK_HISTORY_DISPATCHERS.getPaymentLinksHistory,
      {},
      { root: true }
    )
  },

  onSelectStatus({ commit, dispatch, state }, status) {
    commit('RESET_PAGE')
    commit('SET_SELECTED_STATUS', status)

    let invoiceFormStatuses
    switch (status) {
      case 'active':
        invoiceFormStatuses = ['active']
        break
      case 'finished':
        invoiceFormStatuses = ['expired']
        break
      default:
        invoiceFormStatuses = []
    }

    commit('SET_FILTER', {
      key: 'invoice_form_statuses',
      value: invoiceFormStatuses,
    })

    const allParams = {
      ...state.filters,
      ...(state.search && { search: state.search }),
      ...state.sort,
    }

    if (state.range.length) {
      allParams.created_at_from = state.range[0]
      allParams.created_at_to = state.range[1]
    }

    setQuery(allParams)

    dispatch(
      PAYMENT_LINK_HISTORY_DISPATCHERS.getPaymentLinksHistory,
      {},
      { root: true }
    )
  },

  async onExportToExcel({ state, dispatch }) {
    const [dateFrom, dateTo] = state.range || []

    if (!dateFrom || !dateTo) {
      dispatch(
        TOAST_DISPATCHERS.setErrorMessage,
        {
          title: i18n.global.t(
            'PaymentLinksHistory | export | selectDateRange'
          ),
          message: '',
        },
        { root: true }
      )
      return { success: false }
    }

    try {
      const from = toDateString(dateFrom)
      const to = toDateString(dateTo)
      const language = i18n.global.locale.value === 'ru' ? 'ru' : 'en'

      const response = await paymentLinkHistoryApiService.getExcelReport({
        language,
        from,
        to,
        form_ids: [],
      })

      downloadFile(response, `EMCD-CryptoProcessingReport-${from}-${to}.xlsx`)

      dispatch(
        TOAST_DISPATCHERS.setSuccessMessage,
        { message: i18n.global.t('PaymentLinksHistory | export | success') },
        { root: true }
      )

      return { success: true }
    } catch (error) {
      if (!error.isHandled) {
        dispatch(
          TOAST_DISPATCHERS.setErrorMessage,
          { message: i18n.global.t('PaymentLinksHistory | export | error') },
          { root: true }
        )
      }
      return { success: false }
    }
  },
}
